import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useOnlineStore } from '../stores/onlineStore';
import { useSocketStatusStore } from '../stores/socketStore';
import { PopBrand } from './PopCulture';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, logout } = useAuthStore();
  const { onlineCount, activeGamesCount } = useOnlineStore();
  const socketStatus = useSocketStatusStore(state => state.status);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const header = useRef<HTMLElement>(null);
  const profile = useRef<HTMLDivElement>(null);
  const mobileButton = useRef<HTMLButtonElement>(null);
  const profileButton = useRef<HTMLButtonElement>(null);

  useEffect(() => { setMobileOpen(false); setProfileOpen(false); }, [location.key]);
  useEffect(() => {
    const outside = (event: PointerEvent) => {
      if (!header.current?.contains(event.target as Node)) setMobileOpen(false);
      if (!profile.current?.contains(event.target as Node)) setProfileOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (profileOpen) { setProfileOpen(false); profileButton.current?.focus(); }
      if (mobileOpen) { setMobileOpen(false); mobileButton.current?.focus(); }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape); };
  }, [mobileOpen, profileOpen]);

  const challenges = () => {
    setMobileOpen(false);
    if (!isAuthenticated) {
      window.dispatchEvent(new CustomEvent('open:loginRequired', { detail: { message: t('pop.challengesLogin') } }));
    } else if (location.pathname === '/') window.dispatchEvent(new Event('open:challenges'));
    else navigate('/', { state: { openChallenges: true } });
  };
  const leave = () => { logout(); setProfileOpen(false); setMobileOpen(false); navigate('/'); };
  const language = () => i18n.changeLanguage(i18n.language.startsWith('fr') ? 'en' : 'fr');
  const routes = <>
    <NavLink end to="/">{t('nav.home')}</NavLink>
    <NavLink to="/games">{t('nav.games')}{socketStatus === 'connected' && activeGamesCount > 0 && <span className="qpug-nav-count">{activeGamesCount}</span>}</NavLink>
    <NavLink to="/leaderboard">{t('nav.leaderboard')}</NavLink>
    <button type="button" onClick={challenges}>{t('pop.challenges')}</button>
    <NavLink className="qpug-premium-link" to="/premium">Premium</NavLink>
  </>;

  return <header className="qpug-navbar" ref={header}>
    <div className="qpug-nav-inner qpug-wrap">
      <Link className="qpug-brand-link" to="/" aria-label="Question pour un Geek, accueil"><PopBrand /></Link>
      <div className="qpug-presence" aria-live="polite">
        {socketStatus === 'connected' ? <><i aria-hidden="true" />{onlineCount} {t('pop.online')}</> : <>{t(isAuthenticated ? 'pop.connecting' : 'pop.ready')}</>}
      </div>
      <nav className="qpug-desktop-nav" aria-label={t('pop.mainNavigation')}>
        {routes}
        <button type="button" className="qpug-language" onClick={language} aria-label={t('pop.switchLanguage')}>{i18n.language.startsWith('fr') ? 'EN' : 'FR'}</button>
        {isAuthenticated ? <div className="qpug-account" ref={profile}>
          <button type="button" className="qpug-account-toggle" ref={profileButton} onClick={() => setProfileOpen(value => !value)} aria-expanded={profileOpen} aria-controls="qpug-account-panel"><span className="qpug-avatar-initial" aria-hidden="true">{user?.pseudo?.charAt(0).toUpperCase()}</span><span className="qpug-nav-pseudo">{user?.pseudo}</span><ChevronDown size={14} aria-hidden="true" /></button>
          {profileOpen && <div id="qpug-account-panel" className="qpug-account-panel"><Link to="/profile">{t('nav.profile')}</Link><Link to="/settings">{t('nav.settings')}</Link><Link to="/solo">{t('pop.soloAction')}</Link><button type="button" onClick={leave}>{t('nav.logout')}</button></div>}
        </div> : <Link className="qpug-login-link" to="/login">{t('nav.login')}</Link>}
      </nav>
      <button className="qpug-menu-toggle" type="button" ref={mobileButton} aria-label={t(mobileOpen ? 'pop.closeMenu' : 'pop.openMenu')} aria-expanded={mobileOpen} aria-controls="qpug-mobile-nav" onClick={() => setMobileOpen(value => !value)}>{mobileOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}</button>
    </div>
    {mobileOpen && <nav className="qpug-mobile-nav qpug-wrap" id="qpug-mobile-nav" aria-label={t('pop.mobileNavigation')}>{routes}
      {isAuthenticated ? <><Link to="/solo">{t('pop.soloAction')}</Link><Link to="/profile">{t('nav.profile')}</Link><Link to="/settings">{t('nav.settings')}</Link><button type="button" onClick={leave}>{t('nav.logout')}</button></> : <><Link to="/login">{t('nav.login')}</Link><Link to="/register">{t('nav.register')}</Link></>}
      <button type="button" onClick={language}>{i18n.language.startsWith('fr') ? 'English' : 'Français'}</button>
    </nav>}
  </header>;
}
